import type { PhotoRef } from '@/domain/shared/home-object';
import type { IAwsRecord } from '@/domain/shared/storable';

export const SupportTicketStatus = {
  Open: 'open',
  Closed: 'closed',
} as const;

export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];

export const SupportTicketAwaiting = {
  Support: 'support',
  User: 'user',
  None: 'none',
} as const;

export type SupportTicketAwaiting = (typeof SupportTicketAwaiting)[keyof typeof SupportTicketAwaiting];

export const SupportTicketClosedReason = {
  Resolved: 'resolved',
  ClosedByHomeowner: 'closed_by_homeowner',
  ClosedBySupport: 'closed_by_support',
} as const;

export type SupportTicketClosedReason = (typeof SupportTicketClosedReason)[keyof typeof SupportTicketClosedReason];

export const SupportTicketCategory = {
  Bug: 'bug',
  Account: 'account',
  Billing: 'billing',
  FeatureRequest: 'feature_request',
  IdentityRecovery: 'identity_recovery',
  Other: 'other',
} as const;

export type SupportTicketCategory = (typeof SupportTicketCategory)[keyof typeof SupportTicketCategory];

export const SupportTicketPriority = {
  Low: 'low',
  Normal: 'normal',
  High: 'high',
} as const;

export type SupportTicketPriority = (typeof SupportTicketPriority)[keyof typeof SupportTicketPriority];

export const SupportTicketSource = {
  HomeownerMobile: 'homeowner_mobile',
  HomeownerWeb: 'homeowner_web',
  BusinessWeb: 'business_web',
  RecoveryMobile: 'recovery_mobile',
} as const;

export type SupportTicketSource = (typeof SupportTicketSource)[keyof typeof SupportTicketSource];

export const SupportMessageAuthorType = {
  Homeowner: 'homeowner',
  RecoveryClaimant: 'recovery_claimant',
  Support: 'support',
  Organization: 'organization',
  System: 'system',
} as const;

export type SupportMessageAuthorType = (typeof SupportMessageAuthorType)[keyof typeof SupportMessageAuthorType];

export const SupportTicketContextType = {
  IdentityRecovery: 'identity_recovery',
} as const;

export type SupportTicketContextType = (typeof SupportTicketContextType)[keyof typeof SupportTicketContextType];

export const SupportTicketAccessMode = {
  RecoverySession: 'recovery_session',
} as const;

export type SupportTicketAccessMode = (typeof SupportTicketAccessMode)[keyof typeof SupportTicketAccessMode];

export const SupportTicketTargetType = {
  SystemAdmin: 'system_admin',
  Organization: 'organization',
} as const;

export type SupportTicketTargetType = (typeof SupportTicketTargetType)[keyof typeof SupportTicketTargetType];

export interface SupportRequesterSnapshot {
  userID: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface SupportAppContext {
  platform?: 'ios' | 'android' | 'web' | null;
  appVersion?: string | null;
  buildNumber?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
}

export interface ISupportTicket extends IAwsRecord {
  title: string;
  description?: string | null;
  openedByUserID: string;
  openedBySnapshot: SupportRequesterSnapshot;
  propertyID?: string;
  targetType: SupportTicketTargetType;
  targetID?: string | null;
  status: SupportTicketStatus;
  awaiting: SupportTicketAwaiting;
  closedReason: SupportTicketClosedReason | null;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  source: SupportTicketSource;
  assignedTo?: string | null;
  firstResponseAt?: number | null;
  resolvedAt?: number | null;
  closedAt?: number | null;
  lastMessageAt: number;
  appContext?: SupportAppContext | null;
  latestMessagePreview?: string | null;
  lastMessageAuthorType?: SupportMessageAuthorType | null;
  messageCount: number;
  /** Recovery-ticket routing metadata. It does not grant recovery authority. */
  contextType?: SupportTicketContextType | null;
  contextID?: string | null;
  accessMode?: SupportTicketAccessMode | null;
}

export interface ISupportMessage extends IAwsRecord {
  title?: string | null;
  description?: string | null;
  photos?: PhotoRef[] | null;
  ticketID: string;
  propertyID?: string;
  authorType: SupportMessageAuthorType;
  authorID: string;
  body: string;
  internalOnly?: boolean;
}
